import { CONTACT_DIRECTORY_SCOPE, QUERY_KEYS } from "@/libraries/constant";
import { getIsoDateTime } from "@/libraries/date";
import { randInt } from "@/libraries/helper";
import { MESSAGE_TAG } from "@/schemas";
import { teamService } from "@/services";
import type {
	CaseTeam,
	ContactMemberResponse,
	GetMessageDiscussionsResponse,
	GetTeamResponseType,
	GetTeamsResponse,
	MemberProfileResponse,
	MessageDiscussion,
} from "@/types";
import type { InfiniteData, QueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import omit from "lodash/omit";
import uniqBy from "lodash/uniqBy";
import { generateUUID } from "pubnub";
import { infiniteDataAdd, infiniteDataReplace } from "../utils/mutation";

const createTeam = (queryClient: QueryClient) => {
	queryClient.setMutationDefaults([QUERY_KEYS.MUTATE_TEAM_CREATE], {
		retry: 2,
		mutationFn: (payload: Parameters<typeof teamService.create>[0]) =>
			teamService.create(payload),
		onMutate: (payload) => {
			const infinityMembers = queryClient.getQueryData<
				InfiniteData<ContactMemberResponse>
			>([QUERY_KEYS.GET_MEMBERS, { directory: payload.directory ?? false }]);

			const currentUser = queryClient.getQueryData<MemberProfileResponse>([
				QUERY_KEYS.GET_MEMBER_PROFILE,
			]);

			const members = infinityMembers?.pages?.flatMap((p) => p.users) ?? [];

			if (payload.user_ids.size > 0 && !members?.length) {
				throw new Error("Members for case team is empty");
			}

			const caseTeamUserIds = Array.from(payload.user_ids);
			const pendingInvitations = members?.filter((member) =>
				caseTeamUserIds.includes(member.id),
			);

			const optimisticCaseTeam: CaseTeam = {
				id: randInt(),
				name: payload.name,
				directory: payload.directory ?? false,
				creator: currentUser?.user as CaseTeam["creator"],
				type: "team",
				admins: [],
				users: [currentUser?.user as CaseTeam["users"][number]],
				case_count: 0,
				cases: [],
				member_count: 1,
				pending_invitations: (pendingInvitations ??
					[]) as unknown as CaseTeam["pending_invitations"],
				created_at: getIsoDateTime(),
			};

			const existingAllTeams = queryClient.getQueryData<
				InfiniteData<GetTeamsResponse>
			>([
				QUERY_KEYS.GET_TEAMS,
				{
					directory: payload.directory,
					scope: CONTACT_DIRECTORY_SCOPE.ALL_TEAMS,
				},
			]);

			const existingTeams = queryClient.getQueryData<
				InfiniteData<GetTeamsResponse>
			>([
				QUERY_KEYS.GET_TEAMS,
				{
					directory: payload.directory,
					scope: CONTACT_DIRECTORY_SCOPE.CASE_TEAMS,
				},
			]);

			queryClient.setQueryData<InfiniteData<GetTeamsResponse>>(
				[
					QUERY_KEYS.GET_TEAMS,
					{
						directory: payload.directory,
						scope: CONTACT_DIRECTORY_SCOPE.ALL_TEAMS,
					},
				],
				(oldData) =>
					infiniteDataAdd(oldData, "teams", optimisticCaseTeam as any, true),
			);

			queryClient.setQueryData<InfiniteData<GetTeamsResponse>>(
				[
					QUERY_KEYS.GET_TEAMS,
					{
						directory: payload.directory,
						scope: CONTACT_DIRECTORY_SCOPE.CASE_TEAMS,
					},
				],
				(oldData) =>
					infiniteDataAdd(oldData, "teams", optimisticCaseTeam, true),
			);

			return { optimisticCaseTeam, existingAllTeams, existingTeams };
		},
		onSuccess: (res, _, ctx) => {
			const newTeam = {
				...res.team,
				...omit(ctx.optimisticCaseTeam, ["id"]),
			};
			queryClient.setQueryData<InfiniteData<GetTeamsResponse>>(
				[
					QUERY_KEYS.GET_TEAMS,
					{
						directory: newTeam.directory,
						scope: CONTACT_DIRECTORY_SCOPE.ALL_TEAMS,
					},
				],
				(oldData) =>
					infiniteDataReplace(
						oldData,
						"teams",
						"id",
						ctx.optimisticCaseTeam.id,
						newTeam,
					),
			);

			queryClient.setQueryData<InfiniteData<GetTeamsResponse>>(
				[
					QUERY_KEYS.GET_TEAMS,
					{
						directory: newTeam.directory,
						scope: CONTACT_DIRECTORY_SCOPE.CASE_TEAMS,
					},
				],
				(oldData) =>
					infiniteDataReplace(
						oldData,
						"teams",
						"id",
						ctx.optimisticCaseTeam.id,
						newTeam,
					),
			);

			queryClient.setQueryData([QUERY_KEYS.GET_TEAM, res.team.id], {
				team: newTeam,
			});
		},
		onError: (_error, payload, ctx) => {
			if (!ctx) return;
			queryClient.setQueryData<InfiniteData<GetTeamsResponse>>(
				[
					QUERY_KEYS.GET_TEAMS,
					{
						directory: payload.directory,
						scope: CONTACT_DIRECTORY_SCOPE.ALL_TEAMS,
					},
				],
				ctx.existingAllTeams,
			);

			queryClient.setQueryData<InfiniteData<GetTeamsResponse>>(
				[
					QUERY_KEYS.GET_TEAMS,
					{
						directory: payload.directory,
						scope: CONTACT_DIRECTORY_SCOPE.CASE_TEAMS,
					},
				],
				ctx.existingTeams,
			);
		},
	});
};

const updateTeam = (queryClient: QueryClient) => {
	queryClient.setMutationDefaults([QUERY_KEYS.MUTATE_TEAM_UPDATE], {
		retry: 2,
		mutationFn: (payload: Parameters<typeof teamService.update>[0]) =>
			teamService.update(payload),
		onMutate: async (payload) => {
			await queryClient.cancelQueries({
				queryKey: [QUERY_KEYS.GET_TEAM, payload.id],
			});

			const existingTeam = queryClient.getQueryData<GetTeamResponseType>([
				QUERY_KEYS.GET_TEAM,
				payload.id,
			]);

			const optimisticData: GetTeamResponseType = {
				...existingTeam,
				team: {
					...((existingTeam?.team ?? {}) as GetTeamResponseType["team"]),
					...omit(payload, ["user_ids"]),
				},
			};

			queryClient.setQueryData(
				[QUERY_KEYS.GET_TEAM, payload.id],
				optimisticData,
			);

			return { existingTeam, optimisticData };
		},
		onSuccess: (res, payload, ctx) => {
			if (!ctx) return;

			const updatedTeam: GetTeamResponseType = {
				...ctx.optimisticData,
				team: {
					...ctx.optimisticData.team,
					...res.team,
					pending_invitations: [
						...(ctx.optimisticData.team.pending_invitations ?? []),
						...res.new_members.map((n) => n.user),
					],
					users: ctx.optimisticData.team.users.filter(
						(user) =>
							!res.deleted_members.some((deleted) => deleted.id === user.id),
					),
				},
			};

			queryClient.setQueryData<GetTeamResponseType>(
				[QUERY_KEYS.GET_TEAM, payload.id],
				updatedTeam,
			);
		},
		onError: (_, payload, ctx) => {
			if (!ctx) return;
			queryClient.setQueryData<GetTeamResponseType>(
				[QUERY_KEYS.GET_TEAM, payload.id],
				ctx.existingTeam,
			);
		},
	});
};

const exitFromTeam = (queryClient: QueryClient) => {
	queryClient.setMutationDefaults([QUERY_KEYS.MUTATE_TEAM_LEAVE], {
		retry: 2,
		mutationFn: (payload: Parameters<typeof teamService.exit>[0]) =>
			teamService.exit(payload),
		onMutate: async (teamId) => {
			await queryClient.cancelQueries({
				queryKey: [QUERY_KEYS.GET_TEAM, teamId],
			});

			const existingTeam = queryClient.getQueryData<GetTeamResponseType>([
				QUERY_KEYS.GET_TEAM,
				teamId,
			]);

			const currentUser = queryClient.getQueryData<MemberProfileResponse>([
				QUERY_KEYS.GET_MEMBER_PROFILE,
			]);

			if (!existingTeam || !currentUser?.user?.id) return { existingTeam };

			const optimisticData: GetTeamResponseType = {
				...existingTeam,
				team: {
					...(existingTeam.team ?? {}),
					users: existingTeam.team.users.filter(
						(user) => user.id !== currentUser.user.id,
					),
				},
			};

			queryClient.setQueryData([QUERY_KEYS.GET_TEAM, teamId], optimisticData);

			return { existingTeam, optimisticData };
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.GET_TEAMS],
				exact: false,
			});
		},
		onError: (_, teamId, ctx) => {
			if (!ctx) return;
			queryClient.setQueryData<GetTeamResponseType>(
				[QUERY_KEYS.GET_TEAM, teamId],
				ctx.existingTeam,
			);
		},
	});
};

const assignCaseExternal = (queryClient: QueryClient) => {
	queryClient.setMutationDefaults([QUERY_KEYS.MUTATE_TEAM_CASE_EXTERNAL], {
		retry: 2,
		mutationFn: (
			payload: Parameters<typeof teamService.assignCaseExternal>[0],
		) => teamService.assignCaseExternal(payload),
		onMutate: async (payload) => {
			const queryKeys: Record<"channel_name" | "tags", string | undefined>[] = [
				{
					channel_name: payload.message.channel_name,
					tags: undefined,
				},
				{
					channel_name: payload.message.channel_name,
					tags: MESSAGE_TAG.EXTERNAL,
				},
			];

			for (const queryKey of queryKeys) {
				await queryClient.cancelQueries({
					queryKey: [QUERY_KEYS.GET_MESSAGES, queryKey],
				});
			}

			const currentUser = queryClient.getQueryData<MemberProfileResponse>([
				QUERY_KEYS.GET_MEMBER_PROFILE,
			]);

			if (!currentUser) return;

			const currentDt = dayjs().unix();
			const optimizeMessage: MessageDiscussion = {
				id: payload.message.id ?? generateUUID(),
				channel_name: payload.message.channel_name,
				user: currentUser.user,
				tags: (payload.message.tags as any) || null,
				is_active_user: false,
				accept_at: null,
				decline_at: null,
				system_message: false,
				unread_thread_messages: 0,
				id_db: -1,
				created_at: currentDt,
				updated_at: currentDt,
				reads: [...new Set([currentUser.user.id])],
				text: payload.message.text || null,
				documents: [],
				document_count: 0,
				document_size: "0.0 Kb",
				mentions: [],
				replies: [],
				preview: null,
				sent: false,
				strike: false,
				is_deleted: false,
				delivered: false,
				reply_to: null,
				invitation_status: null,
				latest_reply_date: null,
				reply_count: 0,
				mention_name: [],
				parent: null,
				mentioned_team_name: "...",
				_flag: "sent",
				_isMe: true,
			};

			for (const queryKey of queryKeys) {
				queryClient.setQueryData<InfiniteData<GetMessageDiscussionsResponse>>(
					[QUERY_KEYS.GET_MESSAGES, queryKey],
					(oldData) => infiniteDataAdd(oldData, "messages", optimizeMessage),
				);
			}

			return { queryKeys, optimizeMessage };
		},
		onSuccess: (res, _, ctx) => {
			if (!ctx?.queryKeys) return;
			for (const queryKey of ctx.queryKeys) {
				queryClient.setQueryData<InfiniteData<GetMessageDiscussionsResponse>>(
					[QUERY_KEYS.GET_MESSAGES, queryKey],
					(oldData) =>
						infiniteDataReplace(
							oldData,
							"messages",
							"id",
							ctx.optimizeMessage.id,
							res.message,
						),
				);
			}
			const channelNames = res.message?.channel_name?.split(".");
			const caseId =
				channelNames?.length && channelNames[0].startsWith("case")
					? channelNames.at(-1)
					: undefined;
			if (caseId) {
				queryClient.invalidateQueries({
					queryKey: [QUERY_KEYS.GET_CASE, +caseId],
				});
			}
		},
		onError: (_err, _payload, ctx) => {
			if (!ctx) return;
			for (const queryKey of ctx.queryKeys) {
				queryClient.setQueryData<InfiniteData<GetMessageDiscussionsResponse>>(
					[QUERY_KEYS.GET_MESSAGES, queryKey],
					(oldData) => ({
						...oldData,
						pages:
							oldData?.pages.map((page) => {
								const uniqueMessages = page.messages?.length
									? uniqBy(page.messages, "id")
									: [];
								const messages = uniqueMessages.map((msg) =>
									msg.id === ctx.optimizeMessage.id
										? {
												...msg,
												_flag: "failed" as const,
												_isMe: true,
											}
										: msg,
								);
								return {
									...page,
									messages,
								};
							}) ?? [],
						pageParams: oldData?.pageParams ?? [1],
					}),
				);
			}
		},
	});
};

export const registerTeamMutations = (queryClient: QueryClient) => {
	updateTeam(queryClient);
	exitFromTeam(queryClient);
	createTeam(queryClient);
	assignCaseExternal(queryClient);
	// deleteTeam(queryClient);
	// removeUserFromTeam(queryClient);
};
